import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../../context/ThemeContext';

const industries = [
  {
    icon: 'ri-tools-line',
    title: 'Home Services & Trades',
    desc: 'Plumbers, electricians, HVAC, contractors — never miss a service call again.',
    image: 'https://public.readdy.ai/ai/img_res/165cbcf2e35f4f0313d0445a367c7419.jpg',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Law Firms',
    desc: 'Instantly respond to potential clients and qualify legal inquiries around the clock.',
    image: 'https://readdy.ai/api/search-image?query=modern%20law%20firm%20office%20interior%20dark%20wood%20panels%20premium%20lighting%2C%20professional%20environment%2C%20sophisticated%20aesthetic%2C%20dark%20tones%2C%20no%20text&width=600&height=400&seq=industry_law_1&orientation=landscape',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Wellness & Lifestyle Care',
    desc: 'Spas, clinics, trainers — automate bookings and keep your schedule full.',
    image: 'https://readdy.ai/api/search-image?query=luxury%20wellness%20spa%20interior%20minimalist%20design%20soft%20ambient%20lighting%20clean%20aesthetic%20premium%20photography%20dark%20moody%20atmosphere%20no%20text&width=600&height=400&seq=industry_wellness_1&orientation=landscape',
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Insurance Advisory',
    desc: 'Capture and qualify inbound insurance leads before they reach the competition.',
    image: 'https://readdy.ai/api/search-image?query=professional%20financial%20advisor%20office%20modern%20premium%20interior%20dark%20tones%20clean%20minimal%20design%20sophisticated%20corporate%20environment%20no%20text&width=600&height=400&seq=industry_insurance_1&orientation=landscape',
  },
  {
    icon: 'ri-building-line',
    title: 'Residential Real Estate',
    desc: 'Engage buyer and seller leads instantly and book showings automatically.',
    image: 'https://readdy.ai/api/search-image?query=luxury%20modern%20residential%20property%20exterior%20dusk%20twilight%20premium%20real%20estate%20photography%20dramatic%20dark%20sky%20architectural%20design%20no%20text&width=600&height=400&seq=industry_realestate_1&orientation=landscape',
  },
];

export default function IndustriesSection() {
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLElement>(null);
  const { isDark } = useTheme();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="industries" ref={ref} className={`relative py-32 md:py-44 overflow-hidden ${isDark ? 'bg-[#080808]' : 'bg-white'}`}>
      <div className={`absolute top-0 left-0 right-0 h-px ${isDark ? 'bg-[#1A1A1A]' : 'bg-[#E8E8ED]'}`}></div>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Header */}
        <div
          className="text-center mb-20"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)' }}
        >
          <span className="text-[#3FA9F5] text-xs font-bold tracking-widest uppercase block mb-6">Industries</span>
          <h2
            className={`text-4xl md:text-6xl lg:text-7xl font-black leading-tight mb-6 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}
            style={{ letterSpacing: '-0.03em' }}
          >
            Built for <span className="text-[#3FA9F5]">Service-Based</span> Businesses.
          </h2>
          <p className={`text-lg md:text-xl max-w-xl mx-auto ${isDark ? 'text-[#C9CED6]/60' : 'text-[#6E6E73]'}`} style={{ fontWeight: 300 }}>
            Each system is tailored to your workflow and client journey.
          </p>
        </div>

        {/* Main layout */}
        <div className="grid lg:grid-cols-5 gap-6 items-start">
          {/* Industry tabs */}
          <div className="lg:col-span-2 flex flex-col gap-2">
            {industries.map((ind, i) => (
              <button
                key={ind.title}
                onClick={() => setActive(i)}
                className={`flex items-center gap-4 p-5 rounded-2xl text-left transition-all duration-300 cursor-pointer w-full border ${
                  active === i
                    ? isDark
                      ? 'bg-[#FF2B2B]/8 border-[#FF2B2B]/25 text-white'
                      : 'bg-[#FF2B2B]/6 border-[#FF2B2B]/25 text-[#1D1D1F]'
                    : isDark
                      ? 'bg-[#0D0D0D] border-[#1A1A1A] text-[#C9CED6]/60 hover:border-[#2A2A2A]'
                      : 'bg-[#F5F5F7] border-[#E8E8ED] text-[#6E6E73] hover:border-[#D2D2D7]'
                }`}
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'none' : 'translateX(-20px)',
                  transition: `opacity 0.6s ease ${i * 0.08}s, transform 0.6s ease ${i * 0.08}s, background 0.25s, border-color 0.25s, color 0.25s`,
                }}
              >
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: active === i ? '#FF2B2B20' : isDark ? '#1A1A1A' : '#E8E8ED' }}
                >
                  <i className={`${ind.icon} text-sm`} style={{ color: active === i ? '#FF2B2B' : '#6B7280' }}></i>
                </div>
                <span className="text-sm font-semibold whitespace-nowrap">{ind.title}</span>
                {active === i && <i className="ri-arrow-right-s-line text-[#FF2B2B] ml-auto flex-shrink-0"></i>}
              </button>
            ))}
          </div>

          {/* Detail panel */}
          <div
            className="lg:col-span-3"
            style={{ opacity: visible ? 1 : 0, transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 0.2s' }}
          >
            <div className={`rounded-3xl overflow-hidden border ${isDark ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-[#F5F5F7] border-[#E8E8ED]'}`}>
              <div className="relative w-full" style={{ height: 260 }}>
                <img
                  src={industries[active].image}
                  alt={industries[active].title}
                  className="w-full h-full object-cover object-top transition-all duration-500"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${isDark ? 'from-[#0D0D0D]' : 'from-[#F5F5F7]'} via-transparent to-transparent`}></div>
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#FF2B2B]/12 flex-shrink-0">
                    <i className={`${industries[active].icon} text-[#FF2B2B] text-xl`}></i>
                  </div>
                  <h3 className={`font-black text-xl ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`} style={{ letterSpacing: '-0.02em' }}>{industries[active].title}</h3>
                </div>
                <p className={`text-base leading-relaxed mb-6 ${isDark ? 'text-[#C9CED6]/60' : 'text-[#6E6E73]'}`}>{industries[active].desc}</p>
                <div className="inline-flex items-center gap-2 text-[#FF2B2B] text-sm font-semibold">
                  <span>Custom-built for your workflow</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
